// Dynamic hiring document package flow (per Mudassir's Jun-16 handoff).
// Used by both the HR applicant-detail Documents tab and the applicant portal.
// The package GET response is the single source of truth for what to render.
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

const hiringDocumentPackageService = {
  // HR initiates the package + document rows for an applicant.
  startPackage: async (jobApplicantId) => {
    const res = await axiosInstance.post(API.hiringDocumentPackage.hrStart(jobApplicantId));
    return res.data;
  },

  // Main FE contract: the package + all dynamic document items to render from.
  getByApplicant: async (jobApplicantId) => {
    const res = await axiosInstance.get(
      API.hiringDocumentPackage.getByApplicant(jobApplicantId)
    );
    return res.data;
  },

  // HR submits all required template/source files in one action.
  // itemIds[i] must line up with files[i] (backend matches by index).
  submitTemplates: async (hiringDocumentPackageId, itemIds, files) => {
    const form = new FormData();
    form.append("hiringDocumentPackageId", hiringDocumentPackageId);
    (itemIds || []).forEach((id) => form.append("hiringDocumentItemIds", id));
    (files || []).forEach((file) => form.append("files", file));
    const res = await axiosInstance.post(
      API.hiringDocumentPackage.hrSubmitTemplates,
      form,
      MULTIPART
    );
    return res.data;
  },

  // Applicant submits all required files in one action + HR Policy acknowledgement.
  // HR Policy is acknowledged only (never uploaded back).
  applicantSubmit: async (hiringDocumentPackageId, itemIds, files, acknowledgeHrPolicy) => {
    const form = new FormData();
    form.append("hiringDocumentPackageId", hiringDocumentPackageId);
    (itemIds || []).forEach((id) => form.append("hiringDocumentItemIds", id));
    (files || []).forEach((file) => form.append("files", file));
    form.append("acknowledgeHrPolicy", acknowledgeHrPolicy ? "true" : "false");
    const res = await axiosInstance.post(
      API.hiringDocumentPackage.applicantSubmit,
      form,
      MULTIPART
    );
    return res.data;
  },

  // HR per-document verification (I-9, etc.): in-person check + final signed scan.
  verifyDocument: async (hiringDocumentItemId, inPersonVerified, verificationNotes, finalFile) => {
    const form = new FormData();
    form.append("hiringDocumentItemId", hiringDocumentItemId);
    form.append("inPersonVerified", inPersonVerified ? "true" : "false");
    if (verificationNotes) form.append("verificationNotes", verificationNotes);
    if (finalFile) form.append("finalFile", finalFile);
    const res = await axiosInstance.post(API.hiringDocumentPackage.hrVerify, form, MULTIPART);
    return res.data;
  },

  // HR waives a required document item (counts as completed for the package).
  waiveDocument: async (hiringDocumentItemId) => {
    const res = await axiosInstance.post(
      API.hiringDocumentPackage.hrWaive(hiringDocumentItemId)
    );
    return res.data;
  },
};

export default hiringDocumentPackageService;
